/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Session, type SessionStatus } from '../../lib/api/sessions.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useSessions } from '../../lib/queries/sessions.js';
import { formatIst } from '../../lib/utils/datetime.js';

import { ChildSelect } from './child-select.js';

const STATUS_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'slate',
  no_show: 'slate',
  client_late: 'amber',
  consultant_attended_client_absent: 'rose',
};

/** One Session History row (Figma): date · type · note + status badge. */
function SessionRow({ session }: { readonly session: Session }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-start gap-4">
        <span className="w-16 shrink-0 pt-0.5 text-sm font-medium text-slate-500">
          {formatIst(session.scheduledStart, 'MMM d')}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 dark:text-white">
            {session.sessionType ?? t('parent.sessions.session')}
          </p>
          {session.ratingNote !== null && session.ratingNote !== '' && (
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              {session.ratingNote}
            </p>
          )}
        </div>
        <Badge label={t(`status.${session.status}`)} tone={STATUS_TONE[session.status]} />
      </div>
    </div>
  );
}

/** Parent → Sessions (Figma "Session History"). Read-only, newest first. */
export function ParentSessionsPage(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const sessions = useSessions();
  const [childId, setChildId] = useState('');

  const children = useMemo(() => clients.data ?? [], [clients.data]);
  useEffect(() => {
    if (childId === '' && children.length > 0) {
      setChildId(children[0]?.id ?? '');
    }
  }, [children, childId]);

  const rows = useMemo(
    () =>
      (sessions.data ?? [])
        .filter((s) => s.clientId === childId)
        .slice()
        .sort((a, b) => b.scheduledStart.localeCompare(a.scheduledStart)),
    [sessions.data, childId],
  );

  return (
    <div className="p-8">
      <PageHeader title={t('parent.sessions.title')} subtitle={t('parent.sessions.subtitle')} />
      <div className="mt-4">
        <ChildSelect clients={children} value={childId} onSelect={setChildId} />
      </div>

      <QueryState
        isLoading={sessions.isLoading}
        isError={sessions.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('parent.sessions.noPast')}
      >
        <div className="space-y-3">
          {rows.map((session) => (
            <SessionRow key={session.id} session={session} />
          ))}
        </div>
      </QueryState>
    </div>
  );
}
